import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  useToast
} from '@chakra-ui/react';
import { getFilmSpecificationData, updateFilmSpecificationData } from '../../../services/Films';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues } from '../../../util/helper';
import { getLocationAliasSuggestions } from '../../../services/Location';
import { getColorAliasSuggestions, getLanguageAliasSuggestions } from '../../../services/Color';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";


const Specifications = () => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();


  const schema = {
    "title": "Film Specification fields",
    "description": "",
    "type": "object",
    "properties": {
      "certificate_number": {
        "type": "string",
        "title": "Certificate Number",
      },
      "date_of_issue": {
        "type": "string",
        "title": "Date of Issue"
      },
      "date_of_expiry": {
        "type": "string",
        "title": "Date of Expiry"
      },
      "certificate_image_alias": {
        "type": "string",
        "title": "Certificate Image Alias",
        "default": ""
      },
      "rated": {
        "type": "string",
        "title": "Rating"
      },
      "duration_mins": {
        "type": "string",
        "title": "Duration"
      },
      "lab": {
        "type": "string",
        "title": "Lab"
      },
      "gauge": {
        "type": "string",
        "title": "Gauge"
      },
      "length_of_reels": {
        "type": "string",
        "title": "Length of Reels"
      },
      "shooting_period": {
        "type": "string",
        "title": "Shooting Period"
      },
      "studios": {
        "type": "array",
        "title": "Studios",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      },
      "location": {
        "type": "array",
        "title": "Location",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      },
      "language": {
        "type": "array",
        "title": "Language",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      },
      "color": {
        "type": "array",
        "title": "Color",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      }
    }
  }

  const fetchLocationAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadLocationAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadLocationAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };

  const fetchColorAliasSuggestions = async (inputValue) => {

    const fetchedMasterListAliasSuggestions = await loadColorAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item?.alias,
      value: item?.alias
    }));
  };

  const fetchLanguageAliasSuggestions = async (inputValue) => {
    const fetchedMasterListAliasSuggestions = await loadLanguageAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
      return [];
    }

    return fetchedMasterListAliasSuggestions?.map((item) => ({
      label: item?.alias,
      value: item?.alias
    }));
  };

  const loadColorAliasSuggestions = async (inputValue) => {
    try {
      const result = await getColorAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };
  const loadLanguageAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLanguageAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load document types");
    }
  };

  const widgets = {
    locationAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchLocationAliasSuggestions}
      />
    ),
    colorAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchColorAliasSuggestions}
      />
    ),
    languageAliasTypeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={fetchLanguageAliasSuggestions}
      />
    ),
  };

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    studios: {
      items: {
        alias: {
          "ui:widget": "locationAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
      'ui:classNames': "chakra-form__label"
    },
    location: {
      items: {
        alias: {
          "ui:widget": "locationAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
      'ui:classNames': "chakra-form__label"
    },
    color: {
      items: {
        alias: {
          "ui:widget": "colorAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
      'ui:classNames': "chakra-form__label"
    },
    language: {
      items: {
        alias: {
          "ui:widget": "languageAliasTypeahead", // Use the custom TypeaheadWidget
        },
      },
      'ui:classNames': "chakra-form__label"
    },
    certificate_number: { "ui:emptyValue": "" },
    date_of_issue: { "ui:emptyValue": "" },
    date_of_expiry: { "ui:emptyValue": "" },
    certificate_image_alias: { "ui:emptyValue": "" },
    rated: { "ui:emptyValue": "" },
    duration_mins: { "ui:emptyValue": "" },
    lab: { "ui:emptyValue": "" },
    gauge: { "ui:emptyValue": "" },
    length_of_reels: { "ui:emptyValue": "" },
    shooting_period: { "ui:emptyValue": "" }
  };

  const formData = basicFieldData;

  // Fetch initial data
  useEffect(() => {
    const getSpecificationData = async () => {
      try {
        setIsLoading(true);
        if (alias) {
          const result = await getFilmSpecificationData(alias);
          if (result) {
            setBasicFieldData(transFormData(result));
            setQuickSave(false);
          }
        }
      } catch (error) {
        console.error('Error fetching film data:', error);
                showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getSpecificationData();
  }, [alias, toast, quickSave]);

  const transFormData = (data) => ({
    ...data,
    certificate_image_alias: data?.certificate_image_alias?.[0],
  });

  const reTransFormData = (data) => ({
    ...data,
    certificate_image_alias: [data?.certificate_image_alias],
  });

  // Submit updated data
  const handleSubmit = async (formData) => {
    const originalFormattedData = reTransFormData(formData);
    const deletedValues = getDeletedValues(basicFieldData, originalFormattedData);
    // setIsSubmitting(true);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateFilmSpecificationData(alias, originalFormattedData, deletedValues);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);

                    showSuccessToast(toast, "Data updated successfully");
        }

        // Exit editing mode
        // setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating film data:', error);
            showErrorToast(toast, "Failed to update film data");
    }
    finally {
      setIsSubmitting(false);
    }
  };

  // return (
  //   <Form schema={schema} uiSchema={uiSchema} formData={formData}  validator={validator}  onSubmit={(e) => handleSubmit(e.formData)} widgets={widgets}>
  //     {/* <SubmitButton isLoading={isSubmitting}/> */}
  //   </Form>
  // );

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form schema={schema} uiSchema={uiSchema} formData={formData} validator={validator} onSubmit={(e) => handleSubmit(e.formData)} widgets={widgets}>
          {/* <SubmitButton isLoading={isSubmitting}/> */}
        </Form>
      )}
    </Box>
  );
};

export default Specifications;